/**
 * CardShell — the shared visual container for every node type on the canvas.
 *
 * Implements the n8n-inspired language from FLOWS.md § 11.10:
 *   - rounded corners
 *   - category-colored left accent bar
 *   - category icon in the header
 *   - one-line summary below the title
 *   - subtle shadow, focus ring on selection
 */

import type { ReactNode } from "react";
import { X, type LucideIcon } from "lucide-react";
import { clsx } from "clsx";

export type CategoryKey =
  | "trigger"
  | "routing"
  | "terminal"
  | "postpurchase"
  | "action"
  | "error";

const CATEGORY_STYLES: Record<CategoryKey, { bar: string; icon: string }> = {
  trigger: { bar: "bg-category-trigger", icon: "text-category-trigger" },
  routing: { bar: "bg-category-routing", icon: "text-category-routing" },
  terminal: { bar: "bg-category-terminal", icon: "text-category-terminal" },
  postpurchase: {
    bar: "bg-category-postpurchase",
    icon: "text-category-postpurchase",
  },
  action: { bar: "bg-category-action", icon: "text-category-action" },
  error: { bar: "bg-category-error", icon: "text-category-error" },
};

export interface CardShellProps {
  category: CategoryKey;
  icon: LucideIcon;
  title: string;
  summary: string;
  selected?: boolean;
  children?: ReactNode;
  /** If provided, renders a hover "X" in the top-right corner. */
  onDelete?: () => void;
}

export function CardShell({
  category,
  icon: Icon,
  title,
  summary,
  selected,
  children,
  onDelete,
}: CardShellProps) {
  const styles = CATEGORY_STYLES[category];
  return (
    <div
      className={clsx(
        "group relative flex items-stretch min-w-[240px] max-w-[280px] rounded-lg bg-white dark:bg-neutral-900 shadow-sm border",
        selected
          ? "border-neutral-900 dark:border-neutral-100 ring-2 ring-neutral-900/10 dark:ring-neutral-100/10"
          : "border-neutral-200 dark:border-neutral-700",
      )}
    >
      <div className={clsx("w-1 rounded-l-lg", styles.bar)} aria-hidden />
      <div className="flex-1 px-3 py-2 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className={clsx("w-4 h-4 shrink-0", styles.icon)} aria-hidden />
          <h3 className="font-medium text-sm truncate">{title}</h3>
        </div>
        <p className="mt-0.5 text-xs text-neutral-500 truncate">{summary}</p>
        {children ? <div className="mt-2">{children}</div> : null}
      </div>
      {onDelete ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={clsx(
            "absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white dark:bg-neutral-900",
            "border border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300",
            "shadow-sm flex items-center justify-center",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "hover:bg-category-error hover:text-white hover:border-category-error",
            "nodrag",
          )}
          aria-label="Delete node"
          title="Delete node"
        >
          <X className="w-3 h-3" strokeWidth={2.5} />
        </button>
      ) : null}
    </div>
  );
}
